import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, CreditCard } from 'lucide-react';
import { cancelSubscription, cancelCreateSubscription, createSubscription, getSubscription, upgradeSubscription, verifySignature, getPlanDescriptions } from '../utils/subscriptionApi';
import { deleteMe, logout } from '../utils/authApi';
import ConfirmModal from './ConfirmModal';
import Input from './elements/Input';
import Loading from './elements/Loading';
import Modal from './Modal';
import Button from './elements/Button';
import { toDate } from '../utils/globalUtils';
import { acquireRazorpayScript } from '../utils/razorpayScript';

export default function SubscriptionDetailsModal({ open, onClose, onCancelSubscription }) {
  const licenseData = sessionStorage.getItem('license') ? JSON.parse(sessionStorage.getItem('license')) : null;
  const currentPlanType = licenseData ? licenseData.name : 'FREE';
  const [email] = useState(sessionStorage.getItem('email') || '');
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [actionType, setActionType] = useState(''); // 'cancel' or 'delete'
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showUpgradeSuccessModal, setShowUpgradeSuccessModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // Load Razorpay script
  const loadRazorpayScript = useCallback(() => {
    setRazorpayLoaded(false);
    return acquireRazorpayScript({
      onLoaded: () => setRazorpayLoaded(true),
      onError: () => setRazorpayLoaded(false)
    });
  }, []);

    useEffect(() => {
      if (!open) {
        setRazorpayLoaded(false);
        return;
      }
      const releaseScript = loadRazorpayScript();
      return () => {
        setRazorpayLoaded(false);
        if (typeof releaseScript === 'function') {
          releaseScript();
        }
      };
    }, [open, loadRazorpayScript]);

  useEffect(() => {
    setActionError(null);
    setActionSuccess(false);
  }, []);
  
  useEffect(() => {
    if (!open) {
      return;
    }

    setIsLoading(true);
    getSubscription()
      .then(res => {
        if (res.success) {
          setDetails(res.subscription);
        } else {
          setError('Failed to fetch subscription details.');
        }
        setIsLoading(false);
      })
      .catch(() => {
        setError('Error fetching subscription details.');
        setIsLoading(false);
      });
  }, [open]);

  const allPlans = getPlanDescriptions();

  const canUpgradeTo = (currentPlan, targetPlan) => {
    const planHierarchy = ['FREE', 'SILVER', 'GOLD', 'PLATINUM'];
    const currentIndex = planHierarchy.indexOf(currentPlan?.toUpperCase());
    const targetIndex = planHierarchy.indexOf(targetPlan);
    return targetIndex > currentIndex;
  };

  const handleUpgrade = useCallback(async (newPlan) => {
    setUpgradeLoading(true);
    setActionError(null);
    setActionSuccess(null);

    if (sessionStorage.getItem('isLoggedIn') !== 'true') {
      setActionError('User not authenticated. Please log in again.');
      setUpgradeLoading(false);
      return;
    }

    if (!razorpayLoaded) {
      setActionError('Payment gateway not loaded. Please try again.');
      setUpgradeLoading(false);
      return;
    }

    try {
      const response = await createSubscription(newPlan);
      if (!response.success) {
        setActionError(response.message || 'Could not initialize upgrade. Please try again.');
        setUpgradeLoading(false);
        return;
      }

      const { subscriptionId, key } = response;
      if (!subscriptionId || !key) {
        setActionError('Payment gateway configuration is invalid. Please contact support.');
        setUpgradeLoading(false);
        return;
      }

      // Open Razorpay payment for new subscription
      const options = {
        key,
        subscription_id: subscriptionId,
        name: "Eternal Locker™",
        image: `${window.location.origin}/logo512WithBackground.png`,
        description: `Upgrade to ${newPlan} Plan`,
        handler: async function (resp) {
          try {
            // Verify signature before upgrading
            const verifyRes = await verifySignature(resp.razorpay_payment_id, resp.razorpay_signature);
            if (verifyRes.success) {
              await upgradeSubscription();
              setShowUpgradeSuccessModal(true);
            } else {
              setActionError('Payment signature verification failed. Please contact support.');
            }
          } catch (err) {
            setActionError('Error processing upgrade. Please contact support.');
          }
        },
        prefill: { email: email },
        readonly: { email: true },
        hidden: { contact: true },
        theme: { color: '#000000' },
        modal: {
          escape: false,
          backdropclose: false,
          ondismiss: async function() {
            await cancelCreateSubscription();
            setUpgradeLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (resp) {
        setActionError(`Payment failed: ${resp.error.description}`);
        setUpgradeLoading(false);
      });
      rzp.open();
      // setUpgradeLoading(false);
    } catch (error) {
      await cancelCreateSubscription();
      setActionError('Error upgrading subscription. Please try again.');
      setUpgradeLoading(false);
    }
  }, [email, razorpayLoaded]);

  const handleCancelAndDelete = async (password) => {
    setCancelLoading(true);
    setActionError(null);
    setActionSuccess(null);

    let resCancel = null;
    try {
      resCancel = await cancelSubscription(password);
      if (resCancel.success) {
        setActionSuccess('Subscription cancelled successfully.');
      } else {
        setActionError(resCancel.message || 'Failed to cancel subscription. Please try again later.');
        setPasswordError(resCancel.message || 'Invalid password.');
        setCancelLoading(false);
        return;
      }
    } catch {
      setActionError('Error cancelling subscription. Please try again later.');
      setPasswordError('Error cancelling subscription.');
      setCancelLoading(false);
      return;
    }
    setCancelLoading(false);

    // Proceed to delete account only if cancellation was successful
    if (actionType === 'delete' && resCancel.success) {
      setDeleteLoading(true);
      try {
        const resDelete = await deleteMe(password);
        if (resDelete.success) {
          setActionSuccess('Account deleted successfully.');
        } else {
          setActionError(resDelete.message || 'Failed to delete account. Please try again later.');
          setPasswordError(resDelete.message || 'Invalid password.');
        }
      } catch {
        setActionError('Error deleting account. Please try again later.');
        setPasswordError('Error deleting account.');
      }
      setDeleteLoading(false);
    }

    if (resCancel.success) {
      setPasswordModalOpen(false);
      setShowSuccessModal(true);
    }
  };

  return (
    <>
      {open && (
        <Modal open={open} onClose={onClose} icon={<CreditCard />} title="Subscription Details">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center text-accent-main w-full h-64">
                <Loading className="text-accent-main" /> Loading...
              </div>
            ) : error ? (
              <div className="p-4 text-red-700 bg-red-100 rounded-lg flex items-center"><AlertTriangle className="w-5 h-5 mr-2" />{error}</div>
            ) : !details ? null : (
              <div className="bg-primary-light space-y-6 w-full max-w-3xl mx-auto">
                <div className="text-sm text-primary-dark space-y-1">
                  <div><span className="font-bold">Plan Type:</span> {details.plan.name}</div>
                  <div className="flex items-center"><span className="font-bold">Status:</span> {details.status === 'active' ? <><span className="ml-1 text-green-600">Active</span> <CheckCircle className="w-4 h-4 text-green-600 ml-1" /></> : <span className="ml-1">{details.status}</span>}</div>
                  {details.startDate && <div><span className="font-bold">Start Date:</span> {toDate(details.startDate)}</div>}
                  {details.endDate && <div><span className="font-bold">End Date:</span> {toDate(details.endDate)}</div>}
                </div>
                {details.status === 'active' && (
                  <div className="border-2 border-blue-500 rounded-lg p-4 mb-2 bg-background-light relative">
                    <span className="absolute -top-3 left-4 bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold shadow">Available Plans</span>
                    <div className="text-dark text-sm mb-4">
                      Compare plans and upgrade to unlock more features and storage. Any applicable refund for unused days of your current plan will be processed automatically.
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {Object.keys(allPlans).map(plan => {
                        const isCurrent = plan === currentPlanType?.toUpperCase();
                        const canUpgrade = canUpgradeTo(currentPlanType, plan);
                        return (
                          <div key={plan} className="relative">
                            {isCurrent && (
                              <div className="absolute -top-2 left-4 right-4 bg-green-500 text-white px-2 py-0.5 rounded text-xs font-bold shadow flex items-center z-10 text-center justify-center">
                                Current Plan
                              </div>
                            )}
                            <div 
                              className={`rounded-lg p-3 shadow-sm flex flex-col h-full ${
                                isCurrent 
                                  ? 'bg-accent-dark border-2 border-green-500 sm:pt-3 pt-6' 
                                  : 'bg-background-light border border-blue-300'
                              }`}
                            >
                            <div className="mb-2 mt-2">
                              <div className={`text-lg font-bold ${
                                plan === 'PLATINUM' ? 'text-purple-700' : 
                                plan === 'GOLD' ? 'text-yellow-700' : 
                                plan === 'SILVER' ? 'text-blue-700' : 
                                'text-dark'
                              }`}>{plan}</div>
                              <div className="text-sm font-semibold text-dark">{allPlans[plan]?.price}</div>
                            </div>
                            <ul className="text-xs text-dark space-y-1 mb-3 flex-grow">
                              {allPlans[plan]?.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0 text-green-600" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            <div className="mt-auto">
                              {canUpgrade && (
                                <Button
                                  type='primary'
                                  size='small'
                                  className={`transition duration-200 w-full ${
                                    plan === 'PLATINUM' ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 
                                    plan === 'GOLD' ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-600' : 
                                    'bg-blue-600 hover:bg-blue-700 border-blue-600'
                                  }`}
                                  onClick={() => handleUpgrade(plan)}
                                  loading={upgradeLoading}
                                  disabled={upgradeLoading || !razorpayLoaded}
                                >
                                  Upgrade
                                </Button>
                              )}
                              {isCurrent && (
                                <div className="text-xs text-center text-green-500 font-semibold py-2">
                                {(details && details.url) ? (
                                <a href={details.url} target="_blank" rel="noopener noreferrer">View</a>
                                ) : 'Active'}
                                </div>
                              )}
                            </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {details.status === 'active' && (
                  <>
                    {/* Only cancellation is no longer allowed */}
                    {/* <div className="border-2 border-yellow-500 rounded-lg p-3 mb-2 bg-yellow-50 relative">
                      <span className="absolute -top-3 left-4 bg-yellow-500 text-white px-2 py-0.5 rounded text-xs font-bold shadow">Warning</span>
                      <div className="border-none p-0 bg-transparent text-yellow-800 text-sm flex items-center">
                        Cancelling your subscription will immediately revoke access to your secure locker and all stored data. You will need to re-subscribe to regain access. This action cannot be undone.
                      </div>
                      <Button
                        type='primary'
                        className="mt-3 bg-yellow-500 hover:bg-yellow-600 border-yellow-500 transition duration-200 flex items-center ml-auto"
                        onClick={() => { setActionType('cancel'); setPasswordModalOpen(true); setPassword(''); setPasswordError(null); }}
                        loading={cancelLoading}
                      >
                        Cancel Subscription
                      </Button>
                    </div> */}
                    {/* Dangerous Action Box */}
                    <div className="border-2 border-red-600 rounded-lg p-3 mb-2 bg-red-50 relative">
                      <span className="absolute -top-3 left-4 bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold shadow">Dangerous Action</span>
                      <div className="border-none p-0 bg-transparent text-red-700 text-sm flex items-center">
                        Cancelling and Deleting your account will permanently remove all your data from our servers, including your secure locker contents. This action cannot be undone.
                      </div>
                      <Button
                        type='danger'
                        className="mt-3 bg-red-600 hover:bg-red-700 border-red-600 transition duration-200 flex items-center ml-auto"
                        onClick={() => { setActionType('delete'); setPasswordModalOpen(true); setPassword(''); setPasswordError(null); }}
                        loading={cancelLoading}
                      >
                        Cancel Subscription And Delete My Account
                      </Button>
                    </div>
                    {/* Password Modal for verification */}
                    {passwordModalOpen && (
                      <Modal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} title={`Cancel Subscription${actionType === 'delete' ? ' and Delete Account' : ''}`} icon={<AlertTriangle />}>
                        <div className="flrx-row">
                        <div className="mb-4 text-red-700 bg-red-100 p-3 rounded">
                          <p>If you wish to proceed with this action, please confirm your identity by entering your account password below.</p>
                          <p>This is a security measure to prevent unauthorized cancellations or deletions.</p>
                        </div>
                        <Input
                          label="Password"
                          type="password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          error={passwordError}
                          autoFocus
                        />
                        <div className="flex justify-end mt-4 gap-2">
                          <Button type="secondary" onClick={() => setPasswordModalOpen(false)}>
                            Cancel
                          </Button>
                          <Button
                            type="danger"
                            loading={cancelLoading || deleteLoading}
                            disabled={!password}
                            onClick={async () => {
                              setPasswordError(null);
                              await handleCancelAndDelete(password);
                            }}
                          >
                            Confirm
                          </Button>
                        </div>
                        </div>
                      </Modal>
                    )}
                  </>
                )}
                {actionError && (
                  <div className="p-2 text-red-700 bg-red-100 rounded-lg mt-2 flex items-center"><AlertTriangle className="w-4 h-4 mr-2" />{actionError}</div>
                )}
                {actionSuccess && (
                  <div className="p-2 text-green-700 bg-green-100 rounded-lg mt-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span>{actionSuccess}</span>
                  </div>
                )}
              </div>
            )}
        </Modal>
      )}
      <ConfirmModal
        open={showUpgradeSuccessModal}
        title="Upgrade Successful!"
        message={`Your subscription has been upgraded successfully. You need to log out and log back in for the changes to take effect. Any refund amount, if applicable, will be processed to your original payment method within 5-7 business days`}
        confirmText="Okay"
        onConfirm={async () => {
          await logout();
          sessionStorage.clear();
          window.location.href = '/';
        }}
      />
      <ConfirmModal
        open={showSuccessModal}
        title={`Subscription Cancelled${actionType === 'delete' ? ' and Account Deleted!' : ''}`}
        message={actionType === 'delete' ? 'Your account and all associated data have been deleted successfully. Your subscription has also been cancelled. We\'re sorry to see you go!' : 'Your subscription has been cancelled successfully. Your data is safe and you can re-subscribe anytime to regain access.'}
        confirmText="Okay"
        onConfirm={() => {
            if (onCancelSubscription) {
                onCancelSubscription();
            }
        }}
      />
    </>
  );
}

